import React, { useContext, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import SubscriptionPaymentDialog from './SubscriptionPaymentDialog';
import CompleteProfileDialog from '../../../components/CompleteProfileDialog';
import { UserContext } from '../../../hooks';
import axiosInstance from '../../../services/axiosInstance';
import { toast } from 'react-toastify';
import { getRequiredUserDataMessage } from '../../../utils/validation';

const membershipOptions = [
  {
    id: 'adult',
    name: 'Mayores de 25 años',
    price: 55,
    description: 'Acceso completo y precios reducidos para pistas.',
    benefits: [
      'Reservas ilimitadas de tenis y padel',
      'Acceso a entrenamientos grupales',
      'Acceso a torneos y ligas exclusivos',
      'Bonos de descuento para no socios',
      'Precios reducidos',
    ],
    color: '#4caf50',
  },
  {
    id: 'youth',
    name: 'Menores de 25 años',
    price: 30,
    description: 'Acceso completo y precios reducidos para pistas.',
    benefits: [
      'Reservas ilimitadas de tenis y padel',
      'Acceso a entrenamientos grupales',
      'Acceso a torneos y ligas exclusivos',
      'Asesoramiento para torneos Federaros',
    ],
    color: '#2196f3',
  },
  {
    id: 'padel',
    name: 'Solo Padel',
    price: 30,
    description: 'Acceso solo a pistas de padel.',
    benefits: [
      'Reservas ilimitadas de padel',
      'Torneos exclusivos de padel',
      'Acceso reducido para invitados',
    ],
    color: '#ff9800',
  },
];

const membershipTypeIds = membershipOptions.map(option => option.id);

const Membership = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState('');
  const [intentType, setIntentType] = useState(null);
  const [nextChargeAt, setNextChargeAt] = useState(null);
  const [completeProfileOpen, setCompleteProfileOpen] = useState(false);
  const [pendingMembership, setPendingMembership] = useState(null);
  const { user } = useContext(UserContext);
  const [userMembershipId, setUserMembershipId] = useState(null);

  useEffect(() => {
    if (!user?.subscriptions) {
      setUserMembershipId(null);
      return;
    }

    const activeMembership = user.subscriptions.find(
      sub => sub.status === 'active' && membershipTypeIds.includes(sub.type)
    );

    setUserMembershipId(activeMembership ? activeMembership.type : null);
  }, [user]);

  const startSubscription = async membership => {
    setLoading(membership.id);
    try {
      const response = await axiosInstance.post('/users/create-subscription', {
        priceType: membership.id,
      });
      setClientSecret(response.clientSecret);
      setIntentType(response.intentType || null);
      setNextChargeAt(response.nextChargeAt || null);
      setSelectedOption(membership);
      setDialogOpen(true);
    } catch (error) {
      console.error('Error creando PaymentIntent:', error);
      toast.error(error.response?.data?.message || 'Error al iniciar el alta.');
    } finally {
      setLoading('');
    }
  };

  const handleSubscribe = async membership => {
    if (userMembershipId === membership.id) return;
    const requiredDataMessage = getRequiredUserDataMessage(user);
    if (requiredDataMessage) {
      setPendingMembership(membership);
      setCompleteProfileOpen(true);
      return;
    }

    startSubscription(membership);
  };

  const handleProfileSaved = () => {
    if (pendingMembership) {
      startSubscription(pendingMembership);
      setPendingMembership(null);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setClientSecret('');
    setSelectedOption(null);
    setIntentType(null);
    setNextChargeAt(null);
  };

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        p: 4,
        backgroundColor: '#f9f9f9',
        textAlign: 'center',
        borderRadius: 1,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Elige tu Membresía {user.name}
      </Typography>
      <Typography variant="body1" sx={{ color: '#666', mb: 5 }}>
        Escoge el plan que mejor se adapte a tus necesidades y disfruta de
        beneficios exclusivos como acceso ilimitado y descuentos especiales.
      </Typography>

      <Grid container spacing={4}>
        {membershipOptions.map(option => (
          <Grid item xs={12} sm={6} md={4} key={option.id}>
            <Card
              sx={{
                border: '1px solid #ddd',
                transition: 'all 0.3s ease',
                boxShadow: 'none',
                '&:hover': { transform: 'scale(1.05)' },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}
                >
                  {option.name}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 'bold', color: option.color, mb: 2 }}
                >
                  €{option.price}/mes
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                  {option.description}
                </Typography>
                <List>
                  {option.benefits.map((benefit, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircleOutline sx={{ color: option.color }} />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
              <Box sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => handleSubscribe(option)}
                  disabled={!!userMembershipId}
                >
                  {userMembershipId === option.id ? (
                    'Seleccionado'
                  ) : loading === option.id ? (
                    <CircularProgress size={24} />
                  ) : (
                    'Seleccionar'
                  )}
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <SubscriptionPaymentDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        clientSecret={clientSecret}
        membershipName={selectedOption?.name}
        membershipPrice={selectedOption?.price}
        intentType={intentType}
        nextChargeAt={nextChargeAt}
      />
      <CompleteProfileDialog
        open={completeProfileOpen}
        onClose={() => setCompleteProfileOpen(false)}
        onSaved={handleProfileSaved}
      />
    </Box>
  );
};

export default Membership;
